import {
	KeyRound,
	Mail,
	MessageCircle,
	MessageSquare,
	MessageSquareText,
	Phone,
	Radio,
	Shield,
	Zap,
} from 'lucide-react';
import { Plan } from '@/types/types';

export const serviceTypes: Array<{
	id: string;
	label: string;
	plans: Plan[];
}> = [
	{
		id: 'transactional',
		label: 'Transactional SMS',
		plans: [
			{
				name: 'Starter',
				description: 'Perfect for small businesses and startups',
				monthlyPrice: 5750, // ₹0.115 * 50,000 SMS
				yearlyPrice: 5175,
				pricePerMessage: 0.115,
				minQuantity: '50,000',
				features: [
					'50,000 SMS per month',
					'DLT Registration Support',
					'API Integrations across channels',
					'Message Personalization',
					'24/7 Email Support',
					'Real-time Analytics & Reports',
					'Easy to use Web-Panel',
					'DLT Scrubbing Charges Included',
				],
				color: 'bg-gradient-to-br from-purple-50/80 to-purple-100/80 hover:from-purple-100 hover:to-purple-200',
				shadowColor: 'shadow-purple-100/50',
				borderColor: 'border-purple-200',
				icon: MessageSquare,
				iconColor: 'text-purple-500',
			},
			{
				name: 'Growth',
				description: 'Ideal for growing businesses',
				monthlyPrice: 11000, // ₹0.11 * 100,000 SMS
				yearlyPrice: 9900,
				pricePerMessage: 0.11,
				minQuantity: '100,000',
				features: [
					'100,000 SMS per month',
					'All Starter features',
					'24/7 Email and Phone Support',
					'Priority Support',
					'Advanced Analytics',
					'Custom Integration Support',
					'Enhanced API Access',
					'DLT Scrubbing Charges Included',
				],
				color: 'bg-gradient-to-br from-blue-50/80 to-blue-100/80 hover:from-blue-100 hover:to-blue-200',
				shadowColor: 'shadow-blue-100/50',
				borderColor: 'border-blue-200',
				icon: Zap,
				iconColor: 'text-blue-500',
				popular: true,
			},
			{
				name: 'Enterprise',
				description: 'For large-scale operations',
				monthlyPrice: 50000, // ₹0.10 * 500,000 SMS
				yearlyPrice: 45000,
				pricePerMessage: 0.1,
				minQuantity: '500,000',
				features: [
					'500,000 SMS per month',
					'All Growth features',
					'Dedicated Account Manager',
					'Custom Solutions',
					'Premium API Access',
					'Advanced Analytics Dashboard',
					'Volume-based Pricing',
					'DLT Scrubbing Charges Included',
				],
				color: 'bg-gradient-to-br from-amber-50/80 to-amber-100/80 hover:from-amber-100 hover:to-amber-200',
				shadowColor: 'shadow-amber-100/50',
				borderColor: 'border-amber-200',
				icon: Shield,
				iconColor: 'text-amber-500',
			},
		],
	},
	{
		id: 'otp',
		label: 'OTP SMS',
		plans: [
			{
				name: 'Starter',
				description: 'Secure authentication solution',
				monthlyPrice: 6250, // ₹0.125 * 50,000 SMS
				yearlyPrice: 5625,
				pricePerMessage: 0.125,
				minQuantity: '50,000',
				features: [
					'50,000 OTP SMS per month',
					'DLT Registration Support',
					'API Integrations',
					'Real-time Delivery',
					'24/7 Email Support',
					'Analytics Dashboard',
					'Web Panel Access',
					'DLT Scrubbing Included',
				],
				color: 'bg-gradient-to-br from-green-50/80 to-green-100/80 hover:from-green-100 hover:to-green-200',
				shadowColor: 'shadow-green-100/50',
				borderColor: 'border-green-200',
				icon: KeyRound,
				iconColor: 'text-green-500',
			},
			{
				name: 'Growth',
				description: 'For growing authentication needs',
				monthlyPrice: 11500, // ₹0.115 * 100,000 SMS
				yearlyPrice: 10350,
				pricePerMessage: 0.115,
				minQuantity: '100,000',
				features: [
					'100,000 OTP SMS per month',
					'All Starter features',
					'24/7 Email and Phone Support',
					'Priority Support',
					'Advanced Analytics',
					'Custom Integration Support',
					'Enhanced API Access',
					'DLT Scrubbing Included',
				],
				color: 'bg-gradient-to-br from-blue-50/80 to-blue-100/80 hover:from-blue-100 hover:to-blue-200',
				shadowColor: 'shadow-blue-100/50',
				borderColor: 'border-blue-200',
				icon: Zap,
				iconColor: 'text-blue-500',
			},
			{
				name: 'Enterprise',
				description: 'Enterprise-grade authentication',
				monthlyPrice: 55000, // ₹0.11 * 500,000 SMS
				yearlyPrice: 49500,
				pricePerMessage: 0.11,
				minQuantity: '500,000',
				features: [
					'500,000 OTP SMS per month',
					'All Growth features',
					'Dedicated Manager',
					'Custom Solutions',
					'Premium API Access',
					'Advanced Analytics',
					'Volume Pricing',
					'DLT Scrubbing Included',
				],
				color: 'bg-gradient-to-br from-purple-50/80 to-purple-100/80 hover:from-purple-100 hover:to-purple-200',
				shadowColor: 'shadow-purple-100/50',
				borderColor: 'border-purple-200',
				icon: Shield,
				iconColor: 'text-purple-500',
			},
		],
	},
	{
		id: 'promotional',
		label: 'Promotional SMS',
		plans: [
			{
				name: 'Starter',
				description: 'Effective promotional messaging',
				monthlyPrice: 5750, // ₹0.115 * 50,000 SMS
				yearlyPrice: 5175,
				pricePerMessage: 0.115,
				minQuantity: '50,000',
				features: [
					'50,000 SMS per month',
					'DLT Registration Support',
					'API Integrations',
					'Message Personalization',
					'24/7 Email Support',
					'Real-time Analytics',
					'Web Panel Access',
					'DLT Scrubbing Included',
				],
				color: 'bg-gradient-to-br from-pink-50/80 to-pink-100/80 hover:from-pink-100 hover:to-pink-200',
				shadowColor: 'shadow-pink-100/50',
				borderColor: 'border-pink-200',
				icon: MessageCircle,
				iconColor: 'text-pink-500',
			},
			{
				name: 'Growth',
				description: 'Scale your promotional campaigns',
				monthlyPrice: 11000, // ₹0.11 * 100,000 SMS
				yearlyPrice: 9900,
				pricePerMessage: 0.11,
				minQuantity: '100,000',
				features: [
					'100,000 SMS per month',
					'All Starter features',
					'24/7 Email and Phone Support',
					'Priority Support',
					'Advanced Analytics',
					'Custom Integration',
					'Enhanced API Access',
					'DLT Scrubbing Included',
				],
				color: 'bg-gradient-to-br from-pink-50/80 to-pink-100/80 hover:from-pink-100 hover:to-pink-200',
				shadowColor: 'shadow-pink-100/50',
				borderColor: 'border-pink-200',
				icon: MessageCircle,
				iconColor: 'text-pink-500',
			},
			{
				name: 'Enterprise',
				description: 'Large-scale promotional messaging',
				monthlyPrice: 50000, // ₹0.10 * 500,000 SMS
				yearlyPrice: 45000,
				pricePerMessage: 0.1,
				minQuantity: '500,000',
				features: [
					'500,000 SMS per month',
					'All Growth features',
					'Dedicated Account Manager',
					'Custom Solutions',
					'Premium Support',
					'Advanced Analytics',
					'Volume Pricing',
					'DLT Scrubbing Included',
				],
				color: 'bg-gradient-to-br from-pink-50/80 to-pink-100/80 hover:from-pink-100 hover:to-pink-200',
				shadowColor: 'shadow-pink-100/50',
				borderColor: 'border-pink-200',
				icon: MessageCircle,
				iconColor: 'text-pink-500',
			},
		],
	},
	{
		id: 'whatsapp',
		label: 'WhatsApp API',
		plans: [
			{
				name: 'Basic',
				description: 'Start with WhatsApp Business API',
				monthlyPrice: 999,
				yearlyPrice: 999, // Billed annually
				pricePerMessage: null,
				minQuantity: 'Unlimited',
				features: [
					'Unlimited User-Initiated Conversations',
					'1 User',
					'WhatsApp Broadcast & Scheduler',
					'TPS - 50/sec',
					'Real-time Analytics',
					'Campaign Analytics',
					'Free Green Tick',
					'Template Approval up to 100',
					'APIs & Webhooks',
				],
				color: 'bg-gradient-to-br from-emerald-50/80 to-emerald-100/80 hover:from-emerald-100 hover:to-emerald-200',
				shadowColor: 'shadow-emerald-100/50',
				borderColor: 'border-emerald-200',
				icon: MessageSquareText,
				iconColor: 'text-emerald-500',
			},
			{
				name: 'Pro',
				description: 'Advanced WhatsApp features',
				monthlyPrice: 2499,
				yearlyPrice: 2499, // Billed annually
				pricePerMessage: null,
				minQuantity: 'Unlimited',
				features: [
					'All Basic features',
					'Unlimited Users',
					'TPS - 100/sec',
					'Multi-Agent Live Chat',
					'Template Approval up to 250',
					'Advanced Analytics',
					'Priority Support',
					'Custom Integration',
				],
				color: 'bg-gradient-to-br from-emerald-50/80 to-emerald-100/80 hover:from-emerald-100 hover:to-emerald-200',
				shadowColor: 'shadow-emerald-100/50',
				borderColor: 'border-emerald-200',
				icon: MessageSquareText,
				iconColor: 'text-emerald-500',
				popular: true,
			},
			{
				name: 'Enterprise',
				description: 'Custom WhatsApp solutions',
				monthlyPrice: null,
				yearlyPrice: null,
				pricePerMessage: null,
				minQuantity: 'Custom',
				features: [
					'All Pro features',
					'TPS - 250/sec',
					'Custom Solutions',
					'Dedicated Support',
					'Advanced Integration',
					'Custom Analytics',
					'SLA Guarantee',
					'Priority Features',
				],
				color: 'bg-gradient-to-br from-emerald-50/80 to-emerald-100/80 hover:from-emerald-100 hover:to-emerald-200',
				shadowColor: 'shadow-emerald-100/50',
				borderColor: 'border-emerald-200',
				icon: MessageSquareText,
				iconColor: 'text-emerald-500',
			},
		],
	},
	{
		id: 'rcs',
		label: 'RCS',
		plans: [
			{
				name: 'Starter',
				description: 'Rich Communication Services',
				monthlyPrice: null,
				yearlyPrice: null,
				pricePerMessage: 0.21,
				minQuantity: '50,000',
				volume: '50,000 SMS',
				freeMessages: '2,500 messages on first campaign',
				messagePricing: {
					'Basic RBM Message': '₹0.21',
					'Single RBM Message': '₹0.27',
					'A2P - Conversational RBM Message': '₹0.38',
					'P2A - Conversational RBM Message': '₹0.19',
				},
				otherCharges: {
					'Carrier Maintenance Charge': 'Discounted',
					'One Time Setup': 'Discounted',
					'Platform Processing Fee': 'Included',
					'Bot Development': '₹15,000/man days',
				},
				features: [
					`${50000} SMS Volume`,
					'Rich Media Support',
					'Interactive Buttons',
					'Delivery Reports',
					'Basic Analytics',
					'Email Support',
					'API Access',
					'Basic Templates',
				],
				color: 'bg-gradient-to-br from-violet-50/80 to-violet-100/80 hover:from-violet-100 hover:to-violet-200',
				shadowColor: 'shadow-violet-100/50',
				borderColor: 'border-violet-200',
				icon: Radio,
				iconColor: 'text-violet-500',
			},
			{
				name: 'Growth',
				description: 'Enhanced RCS capabilities',
				monthlyPrice: null,
				yearlyPrice: null,
				pricePerMessage: 0.2,
				minQuantity: '100,000',
				volume: '100,000 SMS',
				freeMessages: '5,000 messages on first campaign',
				messagePricing: {
					'Basic RBM Message': '₹0.20',
					'Single RBM Message': '₹0.25',
					'A2P - Conversational RBM Message': '₹0.37',
					'P2A - Conversational RBM Message': '₹0.17',
				},
				otherCharges: {
					'Carrier Maintenance Charge': 'Discounted',
					'One Time Setup': 'Discounted',
					'Platform Processing Fee': 'Included',
					'Bot Development': '₹15,000/man days',
				},
				features: [
					`${100000} SMS Volume`,
					'Advanced Rich Media',
					'Custom Interactive Elements',
					'Priority Delivery',
					'Advanced Analytics',
					'Priority Support',
					'Enhanced API Access',
					'Custom Templates',
				],
				color: 'bg-gradient-to-br from-violet-50/80 to-violet-100/80 hover:from-violet-100 hover:to-violet-200',
				shadowColor: 'shadow-violet-100/50',
				borderColor: 'border-violet-200',
				icon: Radio,
				iconColor: 'text-violet-500',
				popular: true,
			},
			{
				name: 'Enterprise',
				description: 'Large-scale RCS messaging',
				monthlyPrice: null,
				yearlyPrice: null,
				pricePerMessage: 0.18,
				minQuantity: '500,000',
				volume: '500,000 SMS',
				freeMessages: '25,000 messages on first campaign',
				messagePricing: {
					'Basic RBM Message': '₹0.18',
					'Single RBM Message': '₹0.24',
					'A2P - Conversational RBM Message': '₹0.35',
					'P2A - Conversational RBM Message': '₹0.18',
				},
				otherCharges: {
					'Carrier Maintenance Charge': 'Discounted',
					'One Time Setup': 'Discounted',
					'Platform Processing Fee': 'Included',
					'Bot Development': '₹15,000/man days',
				},
				features: [
					`${500000} SMS Volume`,
					'Premium Rich Media',
					'Advanced Interactive Elements',
					'Priority Delivery',
					'Enterprise Analytics',
					'24/7 Priority Support',
					'Premium API Access',
					'Custom Templates',
				],
				color: 'bg-gradient-to-br from-violet-50/80 to-violet-100/80 hover:from-violet-100 hover:to-violet-200',
				shadowColor: 'shadow-violet-100/50',
				borderColor: 'border-violet-200',
				icon: Radio,
				iconColor: 'text-violet-500',
			},
		],
	},
	{
		id: 'email',
		label: 'Email',
		plans: [
			{
				name: 'Basic',
				description: 'Professional email marketing',
				monthlyPrice: 35000, // ₹0.07 * 500,000
				yearlyPrice: 31500,
				pricePerMessage: 0.07,
				minQuantity: '500,000',
				features: [
					'500,000 emails per month',
					'API Integration across channels',
					'Delivery Optimization Tools',
					'Dynamic Template Editor',
					'Email Personalization',
					'Email Validation Access',
					'24/7 Email Support',
					'Real-time Analytics & Reports',
				],
				color: 'bg-gradient-to-br from-orange-50/80 to-orange-100/80 hover:from-orange-100 hover:to-orange-200',
				shadowColor: 'shadow-orange-100/50',
				borderColor: 'border-orange-200',
				icon: Mail,
				iconColor: 'text-orange-500',
			},
			{
				name: 'Starter',
				description: 'Advanced email capabilities',
				monthlyPrice: 60000, // ₹0.06 * 1,000,000
				yearlyPrice: 54000,
				pricePerMessage: 0.06,
				minQuantity: '1,000,000',
				features: [
					'1,000,000 emails per month',
					'All Basic features',
					'24/7 Email and Phone Support',
					'Priority Delivery',
					'Advanced Template Library',
					'Enhanced Personalization',
					'Priority Email Validation',
					'Advanced Analytics',
				],
				color: 'bg-gradient-to-br from-orange-50/80 to-orange-100/80 hover:from-orange-100 hover:to-orange-200',
				shadowColor: 'shadow-orange-100/50',
				borderColor: 'border-orange-200',
				icon: Mail,
				iconColor: 'text-orange-500',
				popular: true,
			},
			{
				name: 'Enterprise',
				description: 'Custom email solutions',
				monthlyPrice: null,
				yearlyPrice: null,
				pricePerMessage: null,
				minQuantity: 'Custom',
				features: [
					'Custom email volume',
					'All Starter features',
					'Dedicated Support',
					'Custom Integration',
					'Custom Templates',
					'Advanced Security',
					'Custom Analytics',
					'SLA Guarantee',
				],
				color: 'bg-gradient-to-br from-orange-50/80 to-orange-100/80 hover:from-orange-100 hover:to-orange-200',
				shadowColor: 'shadow-orange-100/50',
				borderColor: 'border-orange-200',
				icon: Mail,
				iconColor: 'text-orange-500',
			},
		],
	},
	{
		id: 'ivr',
		label: 'IVR',
		plans: [
			{
				name: 'IVR',
				description: 'Basic IVR solution',
				monthlyPrice: 9500,
				yearlyPrice: 9500,
				validity: '6 Months',
				features: [
					'6 Months Validity',
					'1 Forwarding Number',
					'1000 Min Talktime/Month',
					'1000 Greeting SMS/Month',
					'6 Agents',
					'Unlimited Channels',
					'Multilevel IVR',
				],
				color: 'bg-gradient-to-br from-cyan-50/80 to-cyan-100/80 hover:from-cyan-100 hover:to-cyan-200',
				shadowColor: 'shadow-cyan-100/50',
				borderColor: 'border-cyan-200',
				icon: Phone,
				iconColor: 'text-cyan-500',
			},
			{
				name: 'IVR Plus',
				description: 'Enhanced IVR features',
				monthlyPrice: 19000,
				yearlyPrice: 19000,
				validity: '12 Months',
				features: [
					'12 Months Validity',
					'2 Forwarding Numbers',
					'1000 Min Talktime/Month',
					'1000 Greeting SMS/Month',
					'10 Agents',
					'Unlimited Channels',
					'Multilevel IVR',
				],
				color: 'bg-gradient-to-br from-cyan-50/80 to-cyan-100/80 hover:from-cyan-100 hover:to-cyan-200',
				shadowColor: 'shadow-cyan-100/50',
				borderColor: 'border-cyan-200',
				icon: Phone,
				iconColor: 'text-cyan-500',
				popular: true,
			},
			{
				name: 'IVR Plus+',
				description: 'Premium IVR package',
				monthlyPrice: 48000,
				yearlyPrice: 48000,
				validity: '12 Months',
				features: [
					'12 Months Validity',
					'2 Forwarding Numbers',
					'3500 Min Talktime/Month',
					'3500 Greeting SMS/Month',
					'10 Agents',
					'Unlimited Channels',
					'Multilevel IVR',
				],
				color: 'bg-gradient-to-br from-cyan-50/80 to-cyan-100/80 hover:from-cyan-100 hover:to-cyan-200',
				shadowColor: 'shadow-cyan-100/50',
				borderColor: 'border-cyan-200',
				icon: Phone,
				iconColor: 'text-cyan-500',
			},
		],
	},
];
